import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { CallData } from './CallResultsData';
import CampaignPerformanceReportKPICard from 'components/cards/CampaignPerformanceReportKPICard';
import {
  faPhone,
  faClock,
  faComments
} from '@fortawesome/free-solid-svg-icons';

interface CallKPISectionProps {
  data: CallData[];
}

const CallKPISection: React.FC<CallKPISectionProps> = ({ data }) => {
  // Calculate KPIs

  // 1. Daily calls - Group calls by date
  const callsByDate = data.reduce(
    (acc, call) => {
      if (call.calldatetime) {
        const date = new Date(call.calldatetime).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  // Calculate the average daily calls
  const totalDays = Object.keys(callsByDate).length || 1;
  const totalCalls = data.length;
  const averageDailyCalls = Math.round(totalCalls / totalDays);

  // Prepare data for chart
  const dailyCallsData = Object.entries(callsByDate)
    .map(([date, count]) => ({
      date,
      count
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // 2. Percentage of answered calls by day
  const answerRateByDate = data.reduce(
    (acc, call) => {
      if (call.calldatetime) {
        const date = new Date(call.calldatetime).toISOString().split('T')[0];
        if (!acc[date]) {
          acc[date] = { total: 0, answered: 0 };
        }
        acc[date].total += 1;
        if (call.phoneresult === 'ANSWERED') {
          acc[date].answered += 1;
        }
      }
      return acc;
    },
    {} as Record<string, { total: number; answered: number }>
  );

  // Calculate the average answer rate
  const dailyRates: number[] = [];
  Object.values(answerRateByDate).forEach(({ total, answered }) => {
    if (total > 0) {
      const rate = (answered / total) * 100;
      dailyRates.push(rate);
    }
  });

  // Calcular la media de las tasas diarias con un decimal exacto
  const averageAnswerRate =
    dailyRates.length > 0
      ? parseFloat(
          // eslint-disable-next-line
          (dailyRates.reduce((sum, rate) => sum + rate, 0) / totalDays).toFixed(1)
        )
      : 0.0;

  // Prepare data for chart
  const answerRateData = Object.entries(answerRateByDate)
    .map(([date, { total, answered }]) => ({
      date,
      count: total > 0 ? parseFloat(((answered / total) * 100).toFixed(1)) : 0.0
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // 3. Average call duration by day
  const durationByDate = data.reduce(
    (acc, call) => {
      if (call.calldatetime && call.duration !== undefined) {
        const date = new Date(call.calldatetime).toISOString().split('T')[0];
        if (!acc[date]) {
          acc[date] = { total: 0, count: 0 };
        }
        acc[date].total += call.duration || 0;
        acc[date].count += 1;
      }
      return acc;
    },
    {} as Record<string, { total: number; count: number }>
  );

  // Calculate the average duration
  let totalDuration = 0;
  let validDurationCalls = 0;
  data.forEach(call => {
    if (call.duration !== undefined) {
      totalDuration += call.duration || 0;
      validDurationCalls += 1;
    }
  });
  const averageDuration =
    validDurationCalls > 0 ? Math.round(totalDuration / validDurationCalls) : 0;

  // Prepare data for chart
  const durationData = Object.entries(durationByDate)
    .map(([date, { total, count }]) => ({
      date,
      count: count > 0 ? Math.round(total / count) : 0
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Row className="gy-3 mb-4 justify-content-between bg-body-emphasis">
      <Col xs={12}>
        <Row className="g-3 pt-3 pb-3">
          {/* Daily Calls KPI */}
          <Col xs={12} md={4}>
            <CampaignPerformanceReportKPICard
              icon={faPhone}
              color="primary"
              label="Average"
              title="Daily Calls"
              valuetype="number"
              value={averageDailyCalls}
              data={dailyCallsData}
            />
          </Col>

          {/* Answered Calls Percentage KPI */}
          <Col xs={12} md={4}>
            <CampaignPerformanceReportKPICard
              icon={faComments}
              color="primary"
              label="Average"
              title="% Answered Calls"
              valuetype="percentage"
              value={averageAnswerRate / 100}
              data={answerRateData}
            />
          </Col>

          {/* Average Call Duration KPI */}
          <Col xs={12} md={4}>
            <CampaignPerformanceReportKPICard
              icon={faClock}
              color="primary"
              label="Average"
              title="Call Duration (seconds)"
              valuetype="number"
              value={averageDuration}
              data={durationData}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default CallKPISection;
